import { FC, Fragment, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useEffectOnceWhen } from "rooks";
import { CustomCellRendererProps } from "ag-grid-react";
import { WorkflowNameCellContent } from "./WorkflowNameCellContent";
import { useSchedulePreviewDatesInfoHook } from "../../../../../common/utils/hooks/useSchedulePreviewDatesInfo.hook";
import { WithoutAccountsWorkflowDialog } from "../../../../../common/components/grid/WithoutAccountsWorkflowDialog";
import ActionIcon from "../../../../../../../../assets/icons/workflows/action.svg";
import ActionWithGracePeriodIcon from "../../../../../../../../assets/icons/workflows/action_with_grace_period.svg";
import NotificationIcon from "../../../../../../../../assets/icons/workflows/notification.svg";
import { Schedule } from "@/services/cloudchipr.api";
import { useDialog } from "@/utils/hooks/useDialog.hook";
import { AutomationsFoundResourcesDrawer } from "@/components/pages/automations/common/components/found-resources/AutomationsFoundResourcesDrawer";

export const WorkflowNameCell: FC<CustomCellRendererProps<Schedule>> = ({
  data: schedule,
}) => {
  const [searchParams] = useSearchParams();
  const scheduleIdForPreview = searchParams.get("preview");

  const { open, openDialog, closeDialog } = useDialog();

  const withoutAccounts = useMemo(() => {
    return schedule?.accounts?.every(
      (account) => account.status !== "connected",
    );
  }, [schedule]);

  const accountIds = schedule?.accounts?.map((account) => account.id) ?? [];
  const gracePeriodValue = schedule?.grace_period?.period;

  const { nextRuns, frequency } = useSchedulePreviewDatesInfoHook({
    nextRun: schedule?.metadata?.next_run_at ?? "",
    timeZone: schedule?.time_zone ?? "",
    cron: schedule?.cron ?? null,
    weeklyInterval: schedule?.weekly_schedule_interval,
  });

  useEffectOnceWhen(
    openDialog,
    schedule?.id === scheduleIdForPreview && !withoutAccounts,
  );

  let icon = ActionIcon;
  if (schedule?.action === "notify") {
    icon = NotificationIcon;
  } else if (schedule?.grace_period) {
    icon = ActionWithGracePeriodIcon;
  }

  if (!schedule) {
    return null;
  }

  const content = (
    <WorkflowNameCellContent
      schedule={schedule}
      iconSrc={icon}
      onScheduleResourcesDrawerOpen={openDialog}
    />
  );

  if (withoutAccounts) {
    return (
      <Fragment>
        {content}

        {open && (
          <WithoutAccountsWorkflowDialog
            onClose={closeDialog}
            workflow={schedule as Schedule}
          />
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {content}

      {schedule.filter && (
        <AutomationsFoundResourcesDrawer
          open={open}
          name={schedule.name}
          provider={schedule.cloud_provider}
          onClose={closeDialog}
          accountIds={accountIds}
          status={schedule.status}
          filters={schedule.filter}
          regions={schedule.regions}
          scheduleAction={schedule.action}
          gracePeriodEnabled={!!schedule.grace_period}
          editingUrl={`/automations/workflow/edit/${schedule.id}`}
          nextRuns={nextRuns}
          frequency={frequency}
          gracePeriodValue={gracePeriodValue}
        />
      )}
    </Fragment>
  );
};
